import React from 'react';
import { User, Star, Clock, Wifi, Users, Monitor, MapPin, Heart, Mars, Venus, UserRound } from 'lucide-react';
import verifiedIcon from '@/assets/verified.png';
import type { TutorData } from '@/types/api';
import { groupSubjectsByActualRange } from '@/utils/subjectUtils';
import { getTuitionExperienceShortLabel } from '@/utils/tuitionExperience';
import { useAuthContext } from '@/contexts';
import { useNavigate } from '@/i18n/navigation';
import { useOptionalI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';

interface TutorCardProps {
  tutor: TutorData;
  isWishlisted?: boolean;
  isMutating?: boolean;
  onToggleWishlist?: (tutorId: string, tutorName: string, isWishlisted: boolean) => void;
  source?: string;
  returnSearch?: string;
}

const TutorCard: React.FC<TutorCardProps> = ({
  tutor,
  isWishlisted = false,
  isMutating = false,
  onToggleWishlist,
  source,
  returnSearch,
}) => {
  const { profile } = useAuthContext();
  const { locale } = useOptionalI18n();
  const navigate = useNavigate();
  const isZh = locale === 'zh-CN';
  const isTutor = profile?.role === 'tutor';
  const detailParams = new URLSearchParams();
  if (source) detailParams.set('source', source);
  if (returnSearch) detailParams.set('returnSearch', returnSearch);
  const detailQuery = detailParams.toString();
  const profilePath = `/tutor/${tutor.id}${detailQuery ? `?${detailQuery}` : ''}`;
  const avatarUrl = tutor.profilePhotoUrl || tutor.profilePicture;
  const [imageLoadError, setImageLoadError] = React.useState(false);

  React.useEffect(() => {
    setImageLoadError(false);
  }, [avatarUrl]);

  const tuitionIcon =
    tutor.tuitionType === 'online' ? (
      <Wifi className="h-3.5 w-3.5" />
    ) : tutor.tuitionType === 'physical' ? (
      <Users className="h-3.5 w-3.5" />
    ) : (
      <Monitor className="h-3.5 w-3.5" />
    );

  const tuitionLabel =
    tutor.tuitionType === 'online'
      ? localizeText(locale, 'Online')
      : tutor.tuitionType === 'physical'
      ? localizeText(locale, 'In-person')
      : localizeText(locale, 'Online & In-person');

  const subjectsByLevelRange = tutor.subjectCards
    ? groupSubjectsByActualRange(tutor.subjectCards)
    : [];
  const experienceLabel = getTuitionExperienceShortLabel(tutor.yearsOfTuitionExperience) ?? '—';
  const visibleFeatureTags = tutor.featureTags?.slice(0, 3) ?? [];
  const GenderIcon = tutor.gender === 'male' ? Mars : tutor.gender === 'female' ? Venus : UserRound;
  const genderLabel = tutor.gender === 'male' ? 'Male tutor' : tutor.gender === 'female' ? 'Female tutor' : 'Tutor gender';
  const genderIconClassName = tutor.gender === 'male' ? 'text-blue-600' : tutor.gender === 'female' ? 'text-rose-500' : 'text-gray-500';

  return (
    <div
      onClick={() => navigate(profilePath)}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 overflow-hidden flex flex-col cursor-pointer w-full min-h-[360px] select-none"
    >
      {/* Card Header */}
      <div className="px-5 pt-5 flex items-start gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-[72px] h-[72px] rounded-2xl bg-primary-50 flex items-center justify-center overflow-hidden ring-2 ring-primary-100">
            {avatarUrl && !imageLoadError ? (
              <img
                src={avatarUrl}
                alt={tutor.fullName}
                className="w-full h-full object-cover"
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <User className="h-8 w-8 text-primary-400" />
            )}
          </div>
          {tutor.isFullyVerified && (
            <img src={verifiedIcon} alt="Verified" className="absolute -bottom-1.5 -right-1.5 h-5 w-5" />
          )}
        </div>

        {/* Identity */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-base truncate leading-snug">
                {tutor.fullName}
              </h3>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span
                  aria-label={genderLabel}
                  title={genderLabel}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-100 text-gray-600"
                >
                  <GenderIcon className={`h-3.5 w-3.5 ${genderIconClassName}`} aria-hidden="true" />
                </span>
                {tutor.rating !== undefined && tutor.rating !== null && (
                  <span className="inline-flex items-center gap-0.5 text-xs text-gray-500">
                    <Star className="h-3.5 w-3.5 text-yellow-400 fill-yellow-400" />
                    {(tutor.rating as number).toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            {!isTutor && onToggleWishlist && (
              <button
                onClick={(e) => { e.stopPropagation(); onToggleWishlist(tutor.id, tutor.fullName, isWishlisted); }}
                disabled={isMutating}
                className={`flex-shrink-0 p-1.5 rounded-full transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${
                  isWishlisted
                    ? 'text-red-500 bg-red-50 hover:bg-red-100'
                    : 'text-gray-300 hover:text-red-400 hover:bg-red-50'
                }`}
                title={isWishlisted ? (isZh ? '管理已收藏导师' : 'Manage liked tutor cases') : (isZh ? '加入收藏导师' : 'Add to liked tutors')}
              >
                <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>

          {/* School — 2 lines reserved */}
          <div className="mt-1.5 min-h-[2.8em] space-y-0.5">
            <p className="text-xs text-gray-500 line-clamp-1 leading-[1.4]">
              {tutor.school || ''}
            </p>
            {tutor.major && (
              <p className="text-xs text-gray-400 line-clamp-1 leading-[1.4]">
                {tutor.major}
              </p>
            )}
          </div>
        </div>
      </div>

      {visibleFeatureTags.length > 0 && (
        <div className="px-5 pt-3">
          <div className="flex flex-wrap gap-2">
            {visibleFeatureTags.map((tag) => (
              <span
                key={`${tag.code}-${tag.label}`}
                className="inline-flex items-center rounded-lg border border-tutor-200 bg-tutor-50 px-2.5 py-1 text-[11px] font-semibold leading-4 text-tutor-800"
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="mx-5 mt-4 border-t border-gray-100" />

      {/* Meta Info */}
      <div className="px-5 py-3 flex flex-wrap gap-x-4 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <Clock className="h-3.5 w-3.5 text-primary-400" />
          {experienceLabel} {isZh ? '经验' : 'exp'}
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span className="text-primary-400">{tuitionIcon}</span>
          {tuitionLabel}
        </span>
        {(tutor.preferredState || (tutor.preferredDistricts && tutor.preferredDistricts.length > 0)) && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
            <MapPin className="h-3.5 w-3.5 text-primary-400" />
            <span className="truncate max-w-[140px]">
              {tutor.preferredDistricts && tutor.preferredDistricts.length > 0
                ? localizeStringList(locale, tutor.preferredDistricts.slice(0, 2)).join(', ')
                : tutor.preferredState
                ? localizeText(locale, tutor.preferredState)
                : tutor.preferredState}
              {tutor.preferredDistricts && tutor.preferredDistricts.length > 2 && ` +${tutor.preferredDistricts.length - 2}`}
            </span>
          </span>
        )}
      </div>

      {/* Subjects — sorted by subject count, max 2 levels */}
      {(() => {
        const sorted = [...subjectsByLevelRange].sort((a, b) => b.subjects.length - a.subjects.length);
        const visible = sorted.slice(0, 2);
        const hiddenLevels = sorted.length - visible.length;
        return (
          <div className="px-5 pb-4 overflow-hidden flex flex-col gap-1.5">
            {visible.map(({ groupKey, rangeLabel, subjects }) => {
              const MAX_SUBJECTS = 3;
              const shown = subjects.slice(0, MAX_SUBJECTS);
              const hiddenCount = subjects.length - shown.length;
              return (
                <div key={groupKey} className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 whitespace-nowrap flex-shrink-0">
                    {localizeText(locale, rangeLabel)}
                  </span>
                  <span className="text-gray-600 text-sm truncate flex-1">
                    {localizeStringList(locale, shown).join(', ')}
                  </span>
                  {hiddenCount > 0 && (
                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                      +{hiddenCount}
                    </span>
                  )}
                </div>
              );
            })}
            {hiddenLevels > 0 && (
              <p className="text-[10px] text-gray-400 truncate">
                +{hiddenLevels} {isZh ? '个更多程度' : `more level${hiddenLevels > 1 ? 's' : ''}`}
              </p>
            )}
          </div>
        );
      })()}

      {/* Footer */}
      <div className="mt-auto px-5 py-4 border-t border-gray-100 flex items-center justify-between">
        <div>
          <span className="text-lg font-bold text-primary">
            RM {tutor.hourlyRate ?? '–'}
          </span>
          <span className="text-xs text-gray-400 ml-1">/hr</span>
        </div>
        <span className="text-xs text-gray-400 font-medium">{isZh ? '查看档案 →' : 'View Profile →'}</span>
      </div>
    </div>
  );
};

export default TutorCard;
